import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {makeMapStateToProps} from '../../../../helpers/reselectHelpers';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {AppState} from '../../../../store/appState';
import {getHasPermission} from '../../../../store/data/access/accessSelectors';
import {makeGetAnswerFolder} from '../../../../store/data/answerFolders/answerFolderSelectors';
import {getUserNamespace} from '../../../../store/data/teammates/teammatesSelectors';
import {getUserTeams} from '../../../../store/data/teams/teamsSelectors';
import {AnswerFolderDropdownBox, AnswerFolderDropdownBoxProps} from './answerFolderDropdownBox';

/*
 * Props.
 */

type AnswerFolderDropdownBoxContainerProps = Omit<
  AnswerFolderDropdownBoxProps,
  'selectedFolder' | 'allAvailableNamespaces'
> & {
  selectedFolderId: number | undefined;
  permission: PermissionsEnum;
  resourceNamespace?: string;
};

/*
 * Selectors.
 */

function getSelectedFolderId(_state: AppState, props: AnswerFolderDropdownBoxContainerProps) {
  return props.selectedFolderId;
}

function getPermission(_state: AppState, props: AnswerFolderDropdownBoxContainerProps) {
  return props.permission;
}

function getResourceNamespace(_state: AppState, props: AnswerFolderDropdownBoxContainerProps) {
  return props.resourceNamespace;
}

const getTeams = createSelector(
  [getUserNamespace, getUserTeams, getResourceNamespace, getHasPermission, getPermission],
  (userNamespace, teams, resourceNamespace, hasPermission, permission) => {
    // If this is the user namespace, allow all of our teams.
    if (resourceNamespace === userNamespace) {
      return teams.filter((team) => hasPermission(team.namespace, permission));
    }

    // If this is the namespace of a team that we're part of, use it.
    const team = teams.find(
      (t) => t.namespace === resourceNamespace && hasPermission(t.namespace, permission),
    );
    if (team) {
      return [team];
    }

    // Otherwise, don't allow any team.
    return [];
  },
);

const getNamespacesForUser = createSelector([getUserNamespace, getTeams], (userNamespace, teams) => [
  userNamespace,
  ...teams.map((team) => team.namespace),
]);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<AnswerFolderDropdownBoxContainerProps>()(() => ({
  selectedFolder: makeGetAnswerFolder(getSelectedFolderId),
  allAvailableNamespaces: getNamespacesForUser,
}));

export const AnswerFolderDropdownBoxContainer = connect(mapStateToProps)(AnswerFolderDropdownBox);
